import { useState } from 'react';

import { useFetch } from '../../hooks';
import { getIntegrationInfo } from '../../api';
import { Copy, Section } from '../../components';
import type { IntegrationInfoType } from '../../api';

type IntegrationInfoProps = {
  campaignId: string;
};

const IntegrationInfo: React.FC<IntegrationInfoProps> = ({ campaignId }) => {
  const { data } = useFetch<IntegrationInfoType>(getIntegrationInfo, []);
  const [showKey, setShowKey] = useState(false);

  const apiKey = data?.api_key ?? '';
  const maskedKey = apiKey
    ? `${apiKey.slice(0, 4)}${'•'.repeat(Math.max(0, apiKey.length - 4))}`
    : '';

  const baseUrl = process.env.REACT_APP_API_URL || window.location.origin;
  const curlExample = campaignId
    ? `curl -X POST "${baseUrl}/api/campaigns/${campaignId}/contacts" \\
  -H "Authorization: Bearer ${apiKey || '<API_KEY>'}" \\
  -H "Content-Type: application/json" \\
  -d '{
  "contacts": [
    {
      "email": "john@acme.com",
      "first_name": "John",
      "last_name": "Doe",
      "company": "Acme Corp"
    }
  ],
  "on_duplicate": "skip",
  "auto_start_campaign": false
}'`
    : '';

  return (
    <Section
      title="Integration Info"
      subtitle="Use these values to configure external systems (N8N, Zapier, etc.) to call the import endpoint."
    >
      <div className="flex flex-col gap-y-md">
        {/* API Key */}
        <div className="flex flex-col gap-y-xs">
          <span className="text-small font-medium text-text">API Key</span>
          <div className="flex flex-row items-center gap-x-md">
            <code className="bg-bg text-text text-small font-mono px-md py-sm rounded-sm border border-active flex-grow select-all">
              {showKey ? apiKey : maskedKey}
            </code>
            <button
              onClick={() => setShowKey(v => !v)}
              className="text-small text-textLight hover:text-text cursor-pointer shrink-0"
            >
              {showKey ? 'Hide' : 'Show'}
            </button>
            <Copy text={apiKey} label="Copy" />
          </div>
        </div>

        {/* Campaign ID */}
        <div className="flex flex-col gap-y-xs">
          <span className="text-small font-medium text-text">Campaign ID</span>
          <div className="flex flex-row items-center gap-x-md">
            <code className="bg-bg text-text text-small font-mono px-md py-sm rounded-sm border border-active flex-grow">
              {campaignId || '—  Select a campaign above'}
            </code>
            {campaignId && <Copy text={campaignId} label="Copy" />}
          </div>
        </div>
      </div>

      {/* cURL example */}
      {campaignId && (
        <div className="flex flex-col gap-y-xs mt-sm">
          <div className="flex flex-row items-center justify-between">
            <span className="text-small font-medium text-text">
              Sample cURL Request
            </span>
            <Copy text={curlExample} label="Copy" />
          </div>
          <pre className="bg-bg text-text text-tiny font-mono p-md rounded-sm border border-active overflow-x-auto whitespace-pre-wrap">
            {curlExample}
          </pre>
        </div>
      )}
    </Section>
  );
};

export default IntegrationInfo;
